import AnimatedParent from '../../../../components/essence/animated/animated-parent';
import Page from '../../../../components/essence/page/page';
import EssenceAnimatedButton from '../../../../components/essence/button/animated-button';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useExpenseQueries } from '../../../../shared/tanstack/expenses/queries';
import { useExpenseMutations } from '../../../../shared/tanstack/expenses/mutations';
import useLoaderDisplay from '../../../../shared/hooks/loader-display';
import { useQuery, useMutation } from '@tanstack/react-query';
import accountService from '../../../../shared/services/account-service';
import { InvalidExpenseIdError } from '../../../../shared/models/errors/invalid-expense-id-error';
import styles from './expenses.module.css';
import { useExpenseContext } from '../../../../contexts/expense/expense-context';
import EssenceAlert, { AlertInfo } from '../../../../components/essence/alert/alert';
interface LocationState {
	circleId: string;
}
export default function ExpenseDetail() {
	const { expenseId } = useParams();
	const location = useLocation();
	const { circleId } = location.state as LocationState;
	const { expenseQuery } = useExpenseQueries();
	if (!expenseId) {
		throw new InvalidExpenseIdError();
	}
	const { data: expense, isLoading } = useQuery(expenseQuery(expenseId));
	const { setExpense } = useExpenseContext();

	const navigate = useNavigate();
	const [isCreditor, setIsCreditor] = useState(false);
	const account = accountService.getAccount();
	let loggedInUserId: string | undefined;
	if (account) {
		loggedInUserId = account.id;
	}
	const { deleteExpenseMutation } = useExpenseMutations(circleId);
	const { mutate: deleteExpense, isPaused, status, isSuccess } = useMutation(deleteExpenseMutation);

	const [alert, setAlert] = useState<AlertInfo>({
		show: false,
		message: 'Please try again later.',
	});

	useLoaderDisplay({
		key: 'getExpense',
		isLoading: isLoading,
	});
	useLoaderDisplay({
		key: 'deleteExpense',
		isLoading: status === 'pending' && !isPaused,
	});

	const onEditExpense = () => {
		if (expense) {
			setExpense(expense);
			if (expense.isSettlement && expense.debtors && expense.creditors) {
				navigate(`/secure/circles/${circleId}/add-settlement/payee`);
			} else {
				navigate(`/secure/circles/${circleId}/add-expense/expense-detail`);
			}
		}
	};

	useEffect(() => {
		if (isSuccess) {
			navigate(-1);
		}
	}, [isSuccess, navigate]);

	const onDeleteExpense = () => {
		setAlert({
			show: true,
			message: 'Are you sure you want to delete this expense?',
		});
	};
	useEffect(() => {
		if (expense) {
			setIsCreditor(expense.createdById === loggedInUserId);
		}
	}, [expense, loggedInUserId]);

	const hideAlert = () => {
		setAlert({
			show: false,
			message: alert.message,
		});
	};
	const onDeleteConfirmed = () => {
		hideAlert();
		deleteExpense({ id: expenseId, circleId });
	};

	return (
		<AnimatedParent>
			{alert.show && (
				<EssenceAlert
					onOk={onDeleteConfirmed}
					onDismiss={hideAlert}
					okButton="delete"
					dismissButton="cancel"
					message={alert.message}></EssenceAlert>
			)}
			{expense && (
				<Page title={!expense.isSettlement ? 'expense detail' : 'settlement'}>
					{!expense.isSettlement && <p className="headline-5">Description</p>}
					{!expense.isSettlement && <p className="headline-4">{expense.description}</p>}

					<p className="headline-5  mt-6">Amount</p>
					<p className="headline-4">
						{expense.currency}
						{expense.amount}
					</p>

					<p className="headline-5 mt-6">Date</p>
					<p className="headline-4">
						{expense.dateTime?.toString().replace(/T.*/, '').split('-').reverse().join('-')}
					</p>
					<div className="mt-10"></div>

					{!expense.isSettlement && (
						<div className="mt-6">
							{expense.creditors?.map(user => (
								<p key={user.id} className="headline-4">
									{loggedInUserId !== user.id ? user.name : 'You'} paid {expense.currency}
									{user.amount}{' '}
								</p>
							))}
							<br />
							<br />

							{expense.debtors?.map(user =>
								loggedInUserId !== user.id ? (
									<p key={user.id} className="headline-4">
										- {user.name}&apos;s share {expense.currency}
										{-1 * user.amount}{' '}
									</p>
								) : (
									<p key={user.id} className="headline-4">
										- Your share {expense.currency}
										{-1 * user.amount}{' '}
									</p>
								),
							)}
						</div>
					)}

					{expense.isSettlement && (
						<div className="mt-6">
							{expense.creditors?.map(user => (
								<p key={user.id} className="headline-4">
									{loggedInUserId !== user.id ? user.name : 'You'} paid{' '}
									{expense.debtors?.map(user =>
										loggedInUserId !== user.id ? `${user.name ? user.name : '-'} ` : 'you ',
									)}
									{expense.currency}
									{user.amount}
								</p>
							))}
						</div>
					)}

					<div className="mt-6" />
					{isCreditor && (
						<div className={styles['edit-expense']}>
							<EssenceAnimatedButton name="edit" onClick={onEditExpense}></EssenceAnimatedButton>
							<EssenceAnimatedButton name="delete" onClick={onDeleteExpense}></EssenceAnimatedButton>
						</div>
					)}
				</Page>
			)}
		</AnimatedParent>
	);
}
